import type { BaseUIEvent } from '@base-ui/react/types';
import type { FocusEvent, KeyboardEvent, ReactNode } from 'react';
import type {
  Key as ReactAriaKey,
  TagGroupProps as ReactAriaTagGroupProps,
  TagListProps as ReactAriaTagListProps,
  TagProps as ReactAriaTagProps,
} from 'react-aria-components';
import { Combobox } from '@base-ui/react/combobox';
import { merge, uniqBy } from 'lodash-es';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Button as ReactAriaButton,
  Tag as ReactAriaTag,
  TagGroup as ReactAriaTagGroup,
  TagList as ReactAriaTagList,
} from 'react-aria-components';
import { useDebounce } from 'use-debounce';

import type { DynamicIconName } from '../../assets/DynamicIcon/DynamicIcon';
import type { VariantProp } from '../../common/colors';
import type { MaxWidthTypes } from '../../utilities/shared/MaxWidth';
import type { MinWidthTypes } from '../../utilities/shared/MinWidth';
import type { WidthTypes } from '../../utilities/shared/Width';
import type { ControlSize } from '../shared/types';
import { DynamicIcon } from '../../assets/DynamicIcon/DynamicIcon';
import { LoadingIcon } from '../../assets/LoadingIcon/LoadingIcon';
import { fade, palette } from '../../common/colors';
import { FocusRing } from '../../common/focus_rings';
import { BaseInput } from '../../components/BaseInput/BaseInput';
import {
  ComboBoxList,
  ComboBoxListOptionBody,
  ComboBoxListOptionRoot,
  ComboBoxPopup,
  ComboBoxPortal,
  ComboBoxPositioner,
} from '../../components/shared/ComboBox';
import { colors, darkThemeSelector, fonts, shadows, styled } from '../../stitches.config';
import { BodySansSizes } from '../../text/Body';
import { LargeSansSizes } from '../../text/Large';
import { SmallSansSizes } from '../../text/Small';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { minWidthCSS } from '../../utilities/shared/MinWidth';
import { widthCSS } from '../../utilities/shared/Width';
import { useControlSize } from '../shared/context';
import { selectors, transitions } from '../shared/styles';
import { UtilityControlDecoration } from '../UtilityControl/UtilityControl';

const TokenInputTokenIcon = styled(DynamicIcon, {
  color: '$$iconColor',
});
TokenInputTokenIcon.displayName = 'TokenInputTokenIcon';

const TokenInputTokenValue = styled('span', {
  display: 'flex',
  color: '$$valueColor',
  wordBreak: 'break-word',
});
TokenInputTokenValue.displayName = 'TokenInputTokenValue';

const TokenInputTokenContent = styled(AlignStack);
TokenInputTokenContent.displayName = 'TokenInputTokenContent';

const tokenRemoveOverlayBackground = (color: string) =>
  `linear-gradient(to right, ${fade(color, 0)}, ${color} 20%)`;

const TokenInputTokenRemoveOverlay = styled('span', {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  paddingLeft: '$8',
  background: '$$removeOverlayBackground',
  // Redraws the inner half of the token's stroke, which the background would otherwise cover.
  boxShadow:
    'inset -0.5px 0 0 $$strokeColor, inset 0 0.5px 0 $$strokeColor, inset 0 -0.5px 0 $$strokeColor',
  opacity: '$$tokenInputTokenRemoveOpacity',
  pointerEvents: 'none',
  transition: transitions.control,

  variants: {
    size: {
      'x-small': {
        borderRadius: '0 $4 $4 0',
      },
      small: {
        borderRadius: '0 $4 $4 0',
      },
      medium: {
        paddingRight: '$2',
        borderRadius: '0 $6 $6 0',
      },
      large: {
        paddingRight: '$4',
        borderRadius: '0 $8 $8 0',
      },
      'x-large': {
        paddingRight: '$4',
        borderRadius: '0 $8 $8 0',
      },
    },
  },
});
TokenInputTokenRemoveOverlay.displayName = 'TokenInputTokenRemoveOverlay';

// This is the button; what it holds is only a picture of one. So the focus ring belongs here, on
// the same radius as the chrome it wraps.
const TokenInputTokenRemove = styled(ReactAriaButton, FocusRing, {
  display: 'flex',
  pointerEvents: 'auto',

  variants: {
    size: {
      'x-small': {
        borderRadius: '$4',
      },
      small: {
        borderRadius: '$4',
      },
      medium: {
        borderRadius: '$6',
      },
      large: {
        borderRadius: '$6',
      },
      'x-large': {
        borderRadius: '$6',
      },
    },
  },
});
TokenInputTokenRemove.displayName = 'TokenInputTokenRemove';

const TokenInputTokenRemoveButton = styled(UtilityControlDecoration, {
  // Outranks UtilityControl's fixed placement sizes so the control spans one line of the token.
  '&&': {
    widthAll: '1lh',
    heightAll: '1lh',
  },

  variants: {
    size: {
      'x-small': {
        borderRadius: '$4',
      },
      small: {
        borderRadius: '$4',
      },
      medium: {
        borderRadius: '$6',
      },
      large: {
        borderRadius: '$6',
      },
      'x-large': {
        borderRadius: '$6',
      },
    },
  },
});
TokenInputTokenRemoveButton.displayName = 'TokenInputTokenRemoveButton';

const TokenInputTokenContainer = styled(ReactAriaTag, {
  position: 'relative',
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'row',
  color: '$$valueColor',
  fontFamily: fonts.sans,
  fontWeight: '$regular',
  strokeAll: '$$strokeColor',
  transition: transitions.control,
  $$tokenInputTokenRemoveOpacity: 0,

  [selectors.hover]: {
    $$tokenInputTokenRemoveOpacity: 1,
  },

  [selectors.focus]: {
    outline: 'none',
    boxShadow: shadows.focusRingLight,
    $$tokenInputTokenRemoveOpacity: 1,

    [darkThemeSelector]: {
      boxShadow: shadows.focusRingDark,
    },
  },

  variants: {
    size: {
      'x-small': {
        padding: '0 $2',
        borderRadius: '$4',
        ...SmallSansSizes,
      },
      small: {
        padding: '0 $2',
        borderRadius: '$4',
        ...SmallSansSizes,
      },
      medium: {
        padding: '$2 $6',
        borderRadius: '$6',
        ...BodySansSizes,
      },
      large: {
        padding: '$4 $8',
        borderRadius: '$8',
        ...BodySansSizes,
      },
      'x-large': {
        padding: '$4 $8',
        borderRadius: '$8',
        ...LargeSansSizes,
      },
    },
    variant: {
      brand: {
        background: colors.tokenBgBrandLight,
        $$removeOverlayBackground: tokenRemoveOverlayBackground(palette.tokenBgBrandLight),
        $$strokeColor: colors.tokenStrokeBrandLight,
        $$iconColor: colors.iconBrandLight,
        $$valueColor: colors.bodyBrandLight,
        $$removeColor: colors.iconBrandLight,

        [darkThemeSelector]: {
          background: colors.tokenBgBrandDark,
          $$removeOverlayBackground: tokenRemoveOverlayBackground(palette.tokenBgBrandDark),
          $$strokeColor: colors.tokenStrokeBrandDark,
          $$iconColor: colors.iconBrandDark,
          $$valueColor: colors.bodyBrandDark,
          $$removeColor: colors.iconBrandDark,
        },

        [selectors.hover]: {
          $$iconColor: colors.bodyBrandLight,
          $$valueColor: colors.headingBrandLight,
          $$removeColor: colors.bodyBrandLight,

          [darkThemeSelector]: {
            $$iconColor: colors.bodyBrandDark,
            $$valueColor: colors.headingBrandDark,
            $$removeColor: colors.bodyBrandDark,
          },
        },
      },
      alternative: {
        background: colors.tokenBgAlternativeLight,
        $$removeOverlayBackground: tokenRemoveOverlayBackground(palette.tokenBgAlternativeLight),
        $$strokeColor: colors.tokenStrokeAlternativeLight,
        $$iconColor: colors.iconAlternativeLight,
        $$valueColor: colors.bodyAlternativeLight,
        $$removeColor: colors.iconAlternativeLight,

        [darkThemeSelector]: {
          background: colors.tokenBgAlternativeDark,
          $$removeOverlayBackground: tokenRemoveOverlayBackground(palette.tokenBgAlternativeDark),
          $$strokeColor: colors.tokenStrokeAlternativeDark,
          $$iconColor: colors.iconAlternativeDark,
          $$valueColor: colors.bodyAlternativeDark,
          $$removeColor: colors.iconAlternativeDark,
        },

        [selectors.hover]: {
          $$iconColor: colors.bodyAlternativeLight,
          $$valueColor: colors.headingAlternativeLight,
          $$removeColor: colors.bodyAlternativeLight,

          [darkThemeSelector]: {
            $$iconColor: colors.bodyAlternativeDark,
            $$valueColor: colors.headingAlternativeDark,
            $$removeColor: colors.bodyAlternativeDark,
          },
        },
      },
      attention: {
        background: colors.tokenBgAttentionLight,
        $$removeOverlayBackground: tokenRemoveOverlayBackground(palette.tokenBgAttentionLight),
        $$strokeColor: colors.tokenStrokeAttentionLight,
        $$iconColor: colors.iconAttentionLight,
        $$valueColor: colors.bodyAttentionLight,
        $$removeColor: colors.iconAttentionLight,

        [darkThemeSelector]: {
          background: colors.tokenBgAttentionDark,
          $$removeOverlayBackground: tokenRemoveOverlayBackground(palette.tokenBgAttentionDark),
          $$strokeColor: colors.tokenStrokeAttentionDark,
          $$iconColor: colors.iconAttentionDark,
          $$valueColor: colors.bodyAttentionDark,
          $$removeColor: colors.iconAttentionDark,
        },

        [selectors.hover]: {
          $$iconColor: colors.bodyAttentionLight,
          $$valueColor: colors.headingAttentionLight,
          $$removeColor: colors.bodyAttentionLight,

          [darkThemeSelector]: {
            $$iconColor: colors.bodyAttentionDark,
            $$valueColor: colors.headingAttentionDark,
            $$removeColor: colors.bodyAttentionDark,
          },
        },
      },
      negative: {
        background: colors.tokenBgNegativeLight,
        $$removeOverlayBackground: tokenRemoveOverlayBackground(palette.tokenBgNegativeLight),
        $$strokeColor: colors.tokenStrokeNegativeLight,
        $$iconColor: colors.iconNegativeLight,
        $$valueColor: colors.bodyNegativeLight,
        $$removeColor: colors.iconNegativeLight,

        [darkThemeSelector]: {
          background: colors.tokenBgNegativeDark,
          $$removeOverlayBackground: tokenRemoveOverlayBackground(palette.tokenBgNegativeDark),
          $$strokeColor: colors.tokenStrokeNegativeDark,
          $$iconColor: colors.iconNegativeDark,
          $$valueColor: colors.bodyNegativeDark,
          $$removeColor: colors.iconNegativeDark,
        },

        [selectors.hover]: {
          $$iconColor: colors.bodyNegativeLight,
          $$valueColor: colors.headingNegativeLight,
          $$removeColor: colors.bodyNegativeLight,

          [darkThemeSelector]: {
            $$iconColor: colors.bodyNegativeDark,
            $$valueColor: colors.headingNegativeDark,
            $$removeColor: colors.bodyNegativeDark,
          },
        },
      },
      neutral: {
        background: colors.tokenBgNeutralLight,
        $$removeOverlayBackground: tokenRemoveOverlayBackground(palette.tokenBgNeutralLight),
        $$strokeColor: colors.tokenStrokeNeutralLight,
        $$iconColor: colors.iconNeutralLight,
        $$valueColor: colors.bodyNeutralLight,
        $$removeColor: colors.iconNeutralLight,

        [darkThemeSelector]: {
          background: colors.tokenBgNeutralDark,
          $$removeOverlayBackground: tokenRemoveOverlayBackground(palette.tokenBgNeutralDark),
          $$strokeColor: colors.tokenStrokeNeutralDark,
          $$iconColor: colors.iconNeutralDark,
          $$valueColor: colors.bodyNeutralDark,
          $$removeColor: colors.iconNeutralDark,
        },

        [selectors.hover]: {
          $$iconColor: colors.bodyNeutralLight,
          $$valueColor: colors.headingNeutralLight,
          $$removeColor: colors.bodyNeutralLight,

          [darkThemeSelector]: {
            $$iconColor: colors.bodyNeutralDark,
            $$valueColor: colors.headingNeutralDark,
            $$removeColor: colors.bodyNeutralDark,
          },
        },
      },
      positive: {
        background: colors.tokenBgPositiveLight,
        $$removeOverlayBackground: tokenRemoveOverlayBackground(palette.tokenBgPositiveLight),
        $$strokeColor: colors.tokenStrokePositiveLight,
        $$iconColor: colors.iconPositiveLight,
        $$valueColor: colors.bodyPositiveLight,
        $$removeColor: colors.iconPositiveLight,

        [darkThemeSelector]: {
          background: colors.tokenBgPositiveDark,
          $$removeOverlayBackground: tokenRemoveOverlayBackground(palette.tokenBgPositiveDark),
          $$strokeColor: colors.tokenStrokePositiveDark,
          $$iconColor: colors.iconPositiveDark,
          $$valueColor: colors.bodyPositiveDark,
          $$removeColor: colors.iconPositiveDark,
        },

        [selectors.hover]: {
          $$iconColor: colors.bodyPositiveLight,
          $$valueColor: colors.headingPositiveLight,
          $$removeColor: colors.bodyPositiveLight,

          [darkThemeSelector]: {
            $$iconColor: colors.bodyPositiveDark,
            $$valueColor: colors.headingPositiveDark,
            $$removeColor: colors.bodyPositiveDark,
          },
        },
      },
    },
  },
});
TokenInputTokenContainer.displayName = 'TokenInputTokenContainer';

const getTokenInputTokenIconSize = (size: ControlSize) => {
  switch (size) {
    case 'x-small':
      return 12;
    case 'small':
      return 14;
    case 'medium':
      return 16;
    case 'large':
      return 16;
    case 'x-large':
      return 16;
    default:
      return 16;
  }
};

export type TokenInputTokenProps = Omit<ReactAriaTagProps, 'children'> & {
  /**
   * The contents of the token.
   */
  children?: ReactNode;
  /**
   * The annotation to display on the token.
   */
  annotation?: React.ReactNode;
  /**
   * The icon to display on the token.
   */
  icon?: DynamicIconName;
  /**
   * The size of the token.
   */
  size?: ControlSize;
  /**
   * The variant of the token.
   */
  variant?: VariantProp;
};

const getTokenGap = (size: ControlSize) => {
  switch (size) {
    case 'x-small':
    case 'small':
      return 2;
    case 'large':
    case 'x-large':
      return 6;
    default:
      return 4;
  }
};

const getTokenPreset = (size: ControlSize) => {
  switch (size) {
    case 'x-small':
    case 'small':
      return 'small';
    case 'x-large':
      return 'large';
    default:
      return 'body';
  }
};

function TokenInputToken({
  annotation,
  children,
  icon,
  size = 'medium',
  variant = 'brand',
  ...props
}: TokenInputTokenProps) {
  return (
    <TokenInputTokenContainer textValue={children} variant={variant} size={size} {...props}>
      <TokenInputTokenRemoveOverlay size={size}>
        <TokenInputTokenRemove slot="remove" size={size}>
          <TokenInputTokenRemoveButton icon="trash-can" size={size} variant="brand" />
        </TokenInputTokenRemove>
      </TokenInputTokenRemoveOverlay>
      <TokenInputTokenContent
        direction="row"
        align="center"
        gap={getTokenGap(size)}
        preset={getTokenPreset(size)}
        start={icon && <TokenInputTokenIcon icon={icon} size={getTokenInputTokenIconSize(size)} />}
        end={annotation && annotation}
        wrap="wrap"
      >
        <TokenInputTokenValue>{children}</TokenInputTokenValue>
      </TokenInputTokenContent>
    </TokenInputTokenContainer>
  );
}

const TokenInputTokensList = styled(ReactAriaTagList, {
  display: 'flex',
  flexWrap: 'wrap',
  outline: 'none',

  // Each inset is half the difference between the field's and a token's height, so a single line
  // of tokens sits evenly in the field and wrapped lines keep the same inset.
  variants: {
    size: {
      'x-small': {
        gap: '$4',
        padding: '$2',
      },
      small: {
        gap: '$4',
        padding: '$4',
      },
      medium: {
        gap: '$4',
        padding: '$2',
      },
      large: {
        gap: '$6',
        padding: '$4',
      },
      'x-large': {
        gap: '$8',
        padding: '$6',
      },
    },
  },
});

const TokenInputTokensContainer = styled(ReactAriaTagGroup, {
  display: 'flex',
  alignItems: 'center',
});

interface TokenInputTokensProps<T>
  extends Omit<ReactAriaTagGroupProps, 'children'>,
    Pick<ReactAriaTagListProps<T>, 'items' | 'children' | 'renderEmptyState'> {
  size?: ControlSize;
  variant?: VariantProp;
}

function TokenInputTokens<T extends object>({
  items,
  children,
  renderEmptyState,
  size,
  ...props
}: TokenInputTokensProps<T>) {
  return (
    <TokenInputTokensContainer {...props}>
      <TokenInputTokensList items={items} renderEmptyState={renderEmptyState} size={size}>
        {children as ReactNode | ((item: object) => ReactNode)}
      </TokenInputTokensList>
    </TokenInputTokensContainer>
  );
}

const TokenInputField = styled(BaseInput, {
  display: 'flex',
  flex: 1,
  minWidth: '$120',
});
TokenInputField.displayName = 'TokenInputField';

const TokenInputContainer = styled(Combobox.InputGroup, {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  width: '100%',
  minWidth: 'min-content',
  height: 'min-content',
  background: colors.bgApplicationLight,
  strokeAll: fade(palette.controlStrokeBaseLight, 0.12),
  transition: transitions.control,

  $$valueColor: colors.controlContentValueLight,
  $$placeholderColor: colors.controlContentPlaceholderLight,

  [selectors.focus]: {
    outline: 'none',
  },

  [selectors.focusWithin]: {
    boxShadow: shadows.focusRingLight,

    [darkThemeSelector]: {
      boxShadow: shadows.focusRingDark,
    },
  },

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
    strokeAll: fade(palette.controlStrokeBaseDark, 0.44),

    $$valueColor: colors.controlContentValueDark,
    $$placeholderColor: colors.controlContentPlaceholderDark,
  },

  '&[aria-disabled]': {
    background: colors.gray50,
    strokeAll: fade(palette.controlStrokeBaseLight, 0.3),
    opacity: 0.5,
    pointerEvents: 'none',

    [darkThemeSelector]: {
      background: colors.gray900,
      strokeAll: fade(palette.controlStrokeBaseDark, 0.36),
    },
  },

  variants: {
    isInvalid: {
      true: {
        strokeAllThick: colors.controlStrokeBaseErrorLight,

        [darkThemeSelector]: {
          strokeAll: colors.controlStrokeBaseErrorDark,
        },
      },
    },
    size: {
      'x-small': {
        minHeight: '$20',
        borderRadius: '$6',
      },
      small: {
        minHeight: '$24',
        borderRadius: '$8',
      },
      medium: {
        minHeight: '$28',
        borderRadius: '$8',
      },
      large: {
        minHeight: '$36',
        borderRadius: '$8',
      },
      'x-large': {
        minHeight: '$44',
        borderRadius: '$8',
      },
    },
  },
});
TokenInputContainer.displayName = 'TokenInputContainer';

const TokenInputPopup = styled(ComboBoxPopup, {
  maxWidth: '$420',
  // Radix sets `pointer-events: none` on the body under a modal, and the portalled popup isn't
  // inside its content. See https://github.com/radix-ui/primitives/issues/2122
  pointerEvents: 'auto',
});
TokenInputPopup.displayName = 'TokenInputPopup';

const TokenInputHint = styled('div', {
  ...SmallSansSizes,
  padding: '$4 $8',
  color: colors.controlContentPlaceholderLight,

  [darkThemeSelector]: {
    color: colors.controlContentPlaceholderDark,
  },
});
TokenInputHint.displayName = 'TokenInputHint';

const TokenInputWrapper = styled('div');
TokenInputWrapper.displayName = 'TokenInputWrapper';

export type TokenOption<TValue extends string | number = string> = {
  value: TValue;
  label: string;
  display?: ReactNode;
};

interface CommonTokenInputProps<TValue extends string | number> {
  options?: TokenOption<TValue>[];
  inputProps?: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>;
  tagListProps?: { 'aria-label': string };
  getTokenProps?: (item: TokenOption<TValue>) => TokenInputTokenProps;
  getTokenLabel?: (value: TValue) => string;
  width?: WidthTypes;
  maxWidth?: MaxWidthTypes;
  minWidth?: MinWidthTypes;
  creatable?: boolean | ((inputValue: string) => boolean);
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  invalid?: boolean;
  /**
   * The minimum number of characters required to be input before showing results.
   * To limit number of items rendered in very large lists.
   */
  minSearchLength?: number;
  /**
   * When true, shows a loading spinner inside the input (e.g. while an async
   * typeahead request is in flight).
   */
  loading?: boolean;
}

export type TokenInputProps<TValue extends string | number> = CommonTokenInputProps<TValue> &
  // discriminated union for controlled/uncontrolled TokenInput value
  (| {
        value: TValue[];
        onValueChange: (keys: Set<TValue>) => void;
        defaultValue?: TValue[];
      }
    | {
        defaultValue?: TValue[];
        onValueChange?: undefined;
        value?: undefined;
      }
  ) &
  // discriminated union for controlled/uncontrolled query value
  (| {
        /**
         * the value of the underlying text input. Used for a controlled TokenInput
         * */
        query: string;
        onQueryChange: (value: string) => void;
      }
    | {
        query?: undefined;
        onQueryChange?: undefined;
      }
  ) & {
    size?: ControlSize;
    /**
     * Normalizes text before the `creatable` check and commit (e.g. bare IP -> IP/32);
     * the Add option shows the result.
     */
    normalizeCreatable?: (inputValue: string) => TValue;
    /**
     * Characters that commit the pending text as a token when typed; pasted text is split on them.
     * Defaults to comma, semicolon, and space when `creatable` is set and there are no `options`.
     */
    delimiters?: string[];
    /**
     * Commits pending creatable text when the input closes; Escape still discards it.
     * Defaults to true when `creatable` is set and there are no `options`.
     */
    commitOnBlur?: boolean;
  };

const TOKEN_INPUT_LIST_DELIMITERS = [',', ';', ' '];

type SingleTokenInputProps<TValue extends string | number> = CommonTokenInputProps<TValue> &
  // discriminated union for controlled/uncontrolled TokenInput value
  (| {
        value: TValue | undefined;
        onValueChange: (value: TValue | undefined) => void;
        defaultValue?: TValue;
      }
    | {
        defaultValue?: TValue;
        onValueChange?: undefined;
        value?: undefined;
      }
  ) &
  // discriminated union for controlled/uncontrolled query value
  (| {
        /**
         * the value of the underlying text input. Used for a controlled TokenInput
         * */
        query: string;
        onQueryChange: (value: string) => void;
      }
    | {
        query?: undefined;
        onQueryChange?: undefined;
      }
  ) & {
    size?: ControlSize;
  };

// Positioned absolutely so the spinner never steals width from the `flex: 1` input field.
const TokenInputLoadingAdornment = styled('div', {
  position: 'absolute',
  top: 0,
  bottom: 0,
  right: '$8',
  display: 'flex',
  alignItems: 'center',
  pointerEvents: 'none',
});
TokenInputLoadingAdornment.displayName = 'TokenInputLoadingAdornment';

function TokenInputLoadingSpinner() {
  return (
    <TokenInputLoadingAdornment role="status" aria-label="Loading options">
      <LoadingIcon size={16} variant="neutral" />
    </TokenInputLoadingAdornment>
  );
}

const NO_OPTIONS: never[] = [];

function TokenInputPopover<TValue extends string | number>({
  addValue,
  options,
  minSearchLength,
}: {
  addValue: string | null;
  options: TokenOption<TValue>[];
  minSearchLength: number | null;
}) {
  return (
    <ComboBoxPortal>
      <ComboBoxPositioner align="start" collisionPadding={8} sideOffset={4}>
        <TokenInputPopup>
          <ComboBoxList>
            {addValue != null && (
              <ComboBoxListOptionRoot value={addValue}>
                <ComboBoxListOptionBody>Add "{addValue}"</ComboBoxListOptionBody>
              </ComboBoxListOptionRoot>
            )}
            {options.map((option) => (
              <ComboBoxListOptionRoot key={option.value} value={option.value}>
                <ComboBoxListOptionBody>{option.display ?? option.label}</ComboBoxListOptionBody>
              </ComboBoxListOptionRoot>
            ))}
          </ComboBoxList>
          {minSearchLength != null && (
            <TokenInputHint>
              Please provide at least {minSearchLength}{' '}
              {minSearchLength === 1 ? 'character' : 'characters'} to see results.
            </TokenInputHint>
          )}
        </TokenInputPopup>
      </ComboBoxPositioner>
    </ComboBoxPortal>
  );
}

// `filteredItems` tells Base UI what the list renders, since filtering happens here.
function useItemValues<TValue extends string | number>(
  addValue: string | null,
  options: TokenOption<TValue>[],
) {
  return useMemo(
    (): (TValue | string)[] => [
      ...(addValue != null ? [addValue] : []),
      ...options.map((option) => option.value),
    ],
    [addValue, options],
  );
}

export function TokenInput({
  size = 'medium',
  onValueChange,
  onQueryChange,
  query,
  options,
  creatable,
  inputProps,
  tagListProps,
  getTokenProps = () => ({}),
  getTokenLabel,
  defaultValue: defaultValueProp,
  value,
  width,
  maxWidth,
  minWidth,
  onKeyDown,
  invalid,
  minSearchLength,
  loading,
  normalizeCreatable,
  delimiters: delimitersProp,
  commitOnBlur: commitOnBlurProp,
}: TokenInputProps<string>) {
  const isFreeEntry = !!creatable && options == null;
  const delimiters = delimitersProp ?? (isFreeEntry ? TOKEN_INPUT_LIST_DELIMITERS : undefined);
  const commitOnBlur = commitOnBlurProp ?? isFreeEntry;
  const isControlled = onQueryChange != null;
  const controlSize = useControlSize(size, 'medium');

  const optionsByValue = useMemo(
    () => new Map(options?.map((option) => [option.value, option])),
    [options],
  );

  let defaultValue: TokenOption[] = [];
  if (defaultValueProp) {
    defaultValue = [
      ...defaultValueProp.map((key) => ({
        value: key,
        label: getTokenLabel?.(key) ?? optionsByValue?.get(key)?.label ?? key.toString(),
      })),
    ];
  }
  if (value) {
    defaultValue = Array.from(value).map((key) => ({
      value: key,
      label: getTokenLabel?.(key) ?? optionsByValue?.get(key)?.label ?? key.toString(),
    }));
  }
  const [internalTokens, setInternalTokens] = useState<TokenOption[]>(defaultValue);

  const tokens = value
    ? value.map((v) => ({
        value: v,
        label: getTokenLabel?.(v) ?? optionsByValue?.get(v)?.label ?? v.toString(),
      }))
    : internalTokens;
  const inputRef = useRef<HTMLInputElement>(null);

  const [internalQuery, setInternalQuery] = useState(query ?? '');
  const [debouncedInternalQuery] = useDebounce(query ?? internalQuery, 100);

  const updateQuery = useCallback(
    (val: string) => {
      setInternalQuery(val);
      onQueryChange?.(val);
    },
    [onQueryChange],
  );

  const handleOnChange = useCallback(
    (values: TokenOption[]) => {
      const deduped = uniqBy(values, (token) => token.value);
      setInternalTokens(deduped);
      if (onValueChange) {
        onValueChange(new Set(deduped.map((token) => token.value)));
      }
      updateQuery('');
    },
    [setInternalTokens, onValueChange, updateQuery],
  );

  const resolveCreatable = useCallback(
    (part: string): string | null => {
      const candidate = normalizeCreatable ? normalizeCreatable(part) : part;
      if (!candidate) return null;
      const canCreate = typeof creatable === 'function' ? creatable(candidate) : !!creatable;
      return canCreate ? candidate : null;
    },
    [creatable, normalizeCreatable],
  );

  const splitPattern = useMemo(() => {
    if (!delimiters?.length) return null;
    const escaped = delimiters.map((d) => d.replace(/[\\\]^-]/g, '\\$&')).join('');
    return new RegExp(`[${escaped}]`);
  }, [delimiters]);

  const tokenForValue = useCallback(
    (val: string) => ({
      value: val,
      label: getTokenLabel?.(val) ?? optionsByValue?.get(val)?.label ?? val.toString(),
    }),
    [getTokenLabel, optionsByValue],
  );

  // Returns the text that couldn't be committed.
  const commitPending = useCallback(
    (raw: string): string => {
      const parts = (splitPattern ? raw.split(splitPattern) : [raw])
        .map((part) => part.trim())
        .filter(Boolean);
      const created: TokenOption[] = [];
      const leftover: string[] = [];
      for (const part of parts) {
        const resolved = resolveCreatable(part);
        if (resolved) {
          created.push(tokenForValue(resolved));
        } else {
          leftover.push(part);
        }
      }
      if (created.length > 0) {
        handleOnChange([...tokens, ...created]);
      }
      return leftover.join(' ');
    },
    [splitPattern, resolveCreatable, tokenForValue, handleOnChange, tokens],
  );

  const onRemove = useCallback(
    (keys: Set<ReactAriaKey>) => {
      setInternalTokens((prevTokens) =>
        Array.from(prevTokens).filter((item) => !keys.has(item.value)),
      );
      const newVal = new Set(
        tokens.map((token) => token.value).filter((tokenId) => !keys.has(tokenId)),
      );
      if (onValueChange) {
        onValueChange(newVal);
      }
      if (newVal.size === 0 && inputRef?.current) {
        inputRef.current.focus();
      }
    },
    [tokens, onValueChange],
  );

  const pending = query ?? internalQuery;
  const [open, setOpen] = useState(false);
  const highlightedRef = useRef<string | undefined>(undefined);

  const dismiss = useCallback(
    (discard: boolean) => {
      setOpen(false);
      if (commitOnBlur && !discard && pending.trim()) {
        commitPending(pending);
      }
      updateQuery('');
    },
    [commitOnBlur, pending, commitPending, updateQuery],
  );

  const handleValueChange = useCallback(
    (values: string[]) => {
      const tokensByValue = new Map(tokens.map((token) => [token.value, token]));
      handleOnChange(values.map((val) => tokensByValue.get(val) ?? tokenForValue(val)));
    },
    [tokens, tokenForValue, handleOnChange],
  );

  const handleInputValueChange = useCallback(
    (next: string, details: Combobox.Root.ChangeEventDetails) => {
      if (details.reason === 'input-change') {
        updateQuery(splitPattern?.test(next) ? commitPending(next) : next);
        // Base UI only opens on real typing (`inputType`), not on programmatic input.
        if (next.trim()) setOpen(true);
      } else if (next === '') {
        updateQuery('');
      } else {
        details.cancel();
      }
    },
    [splitPattern, commitPending, updateQuery],
  );

  const handleOpenChange = useCallback(
    (next: boolean, details: Combobox.Root.ChangeEventDetails) => {
      if (next) {
        setOpen(true);
      } else if (details.reason === 'item-press') {
        details.cancel();
      } else {
        dismiss(details.reason === 'escape-key');
      }
    },
    [dismiss],
  );

  const filteredOptions = useMemo(() => {
    if (isControlled || !debouncedInternalQuery) return options;

    const lowerCaseQuery = debouncedInternalQuery.toLowerCase();

    return (
      options
        ?.filter((option) => option.label.toLowerCase().includes(lowerCaseQuery))
        // Only show up to 100 options
        .slice(0, 100)
    );
  }, [isControlled, options, debouncedInternalQuery]);

  const createValue = useMemo(() => {
    const trimmed = pending.trim();
    if (!trimmed) return null;
    return resolveCreatable(trimmed);
  }, [pending, resolveCreatable]);

  // The Add option's label and value stay live so it always offers exactly what will be
  // committed; only its appearance is debounced, so the popover doesn't churn on the
  // transient validity flips of a value being typed.
  const [debouncedCanCreate] = useDebounce(createValue != null, 100);
  const addValue =
    debouncedCanCreate &&
    createValue != null &&
    !tokens.some((token) => token.value === createValue)
      ? createValue
      : null;
  const showHint = !!minSearchLength && debouncedInternalQuery.length < minSearchLength;
  const listedOptions = (!showHint && filteredOptions) || NO_OPTIONS;
  const itemValues = useItemValues(addValue, listedOptions);
  const hasContent = (filteredOptions?.length ?? 0) > 0 || addValue != null;

  const handleInputKeyDown = useCallback(
    (event: BaseUIEvent<KeyboardEvent<HTMLInputElement>>) => {
      if (event.key === 'Escape') {
        // Base UI's own Escape clears every token while the list is closed.
        event.preventBaseUIHandler();
        if (open) {
          if (hasContent) event.stopPropagation();
          dismiss(true);
        }
      } else if (
        event.key === 'Enter' &&
        creatable &&
        highlightedRef.current === undefined &&
        pending.trim()
      ) {
        // Commit directly; letting Base UI close would wipe uncreatable text, and a debounced Add
        // option mounts too late for Base UI's typing-time auto-highlight.
        event.preventDefault();
        event.preventBaseUIHandler();
        const trimmed = pending.trim();
        const leftover = commitPending(trimmed);
        if (leftover !== trimmed) {
          updateQuery(leftover);
        }
      }
      inputProps?.onKeyDown?.(event);
    },
    [open, hasContent, dismiss, creatable, pending, commitPending, updateQuery, inputProps],
  );

  const handleInputFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      setOpen(true);
      inputProps?.onFocus?.(event);
    },
    [inputProps],
  );

  // While the popup is mounted, Base UI reports focus leaving as a `focus-out` close.
  const handleInputBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      if (open && !hasContent) dismiss(false);
      inputProps?.onBlur?.(event);
    },
    [open, hasContent, dismiss, inputProps],
  );

  return (
    <Combobox.Root<string, true>
      multiple
      autoHighlight
      disabled={inputProps?.disabled}
      open={open}
      onOpenChange={handleOpenChange}
      value={tokens.map((token) => token.value)}
      onValueChange={handleValueChange}
      inputValue={pending}
      onInputValueChange={handleInputValueChange}
      onItemHighlighted={(item) => {
        highlightedRef.current = item;
      }}
      filteredItems={itemValues}
    >
      <TokenInputWrapper
        onKeyDown={onKeyDown}
        css={merge(widthCSS(width), maxWidthCSS(maxWidth), minWidthCSS(minWidth))}
      >
        <TokenInputContainer size={controlSize} isInvalid={invalid}>
          {tokens.length > 0 && (
            <TokenInputTokens {...tagListProps} items={tokens} onRemove={onRemove} size={size}>
              {(item) => (
                <TokenInputToken
                  {...getTokenProps(item)}
                  id={item.value}
                  key={item.value}
                  size={controlSize}
                >
                  {item.label}
                </TokenInputToken>
              )}
            </TokenInputTokens>
          )}
          <TokenInputField
            size={controlSize}
            isNested
            shouldApplyFocusStyles={false}
            inputRef={inputRef}
            inputProps={{
              ...inputProps,
              placeholder: inputProps?.placeholder ?? 'Select...',
              as: Combobox.Input,
              onKeyDown: handleInputKeyDown,
              onFocus: handleInputFocus,
              onBlur: handleInputBlur,
              style: loading ? { ...inputProps?.style, paddingRight: 28 } : inputProps?.style,
            }}
          />
          {loading ? <TokenInputLoadingSpinner /> : null}
        </TokenInputContainer>
      </TokenInputWrapper>
      {hasContent && (
        <TokenInputPopover
          addValue={addValue}
          options={listedOptions}
          minSearchLength={showHint ? minSearchLength : null}
        />
      )}
    </Combobox.Root>
  );
}

const SingleTokenInputTokensContainer = styled(TokenInputTokensContainer, {
  width: '100%',
});
SingleTokenInputTokensContainer.displayName = 'SingleTokenInputTokensContainer';

const SingleTokenInputTokensList = styled(TokenInputTokensList, {
  width: '100%',
});
SingleTokenInputTokensList.displayName = 'SingleTokenInputTokensList';

function SingleTokenInputTokens<T extends object>({
  items,
  children,
  renderEmptyState,
  size,
  ...props
}: TokenInputTokensProps<T>) {
  return (
    <SingleTokenInputTokensContainer {...props}>
      <SingleTokenInputTokensList items={items} renderEmptyState={renderEmptyState} size={size}>
        {children as ReactNode}
      </SingleTokenInputTokensList>
    </SingleTokenInputTokensContainer>
  );
}

const SingleTokenInputToken = styled(TokenInputToken, {
  padding: '$8',
});
SingleTokenInputToken.displayName = 'SingleTokenInputToken';

export function SingleTokenInput<TValue extends string | number>({
  size = 'medium',
  onValueChange,
  onQueryChange,
  query,
  options,
  creatable = false,
  inputProps,
  tagListProps,
  getTokenProps = () => ({}),
  getTokenLabel,
  defaultValue: defaultValueProp,
  value,
  width,
  maxWidth,
  minWidth,
  onKeyDown,
  minSearchLength,
  loading,
}: SingleTokenInputProps<TValue>) {
  const isControlled = onQueryChange != null;
  const controlSize = useControlSize(size, 'medium');

  let defaultValue: TokenOption<TValue> | undefined;
  if (defaultValueProp != null && defaultValueProp !== '') {
    defaultValue = {
      value: defaultValueProp,
      label: getTokenLabel ? getTokenLabel(defaultValueProp) : defaultValueProp.toString(),
    };
  }
  if (value != null && value !== '') {
    defaultValue = {
      value,
      label: getTokenLabel ? getTokenLabel(value) : value.toString(),
    };
  }
  const [internalToken, setInternalToken] = useState<TokenOption<TValue> | undefined>(defaultValue);
  const token: TokenOption<TValue> | undefined = useMemo(() => {
    if (value != null && value !== '') {
      return (
        options?.find((opt) => opt.value === value) ?? {
          value,
          label: getTokenLabel ? getTokenLabel(value) : value.toString(),
        }
      );
    }
    return onValueChange ? undefined : internalToken;
  }, [value, options, internalToken, getTokenLabel, onValueChange]);
  const inputRef = useRef<HTMLInputElement>(null);

  const [internalQuery, setInternalQuery] = useState(query ?? '');
  const [debouncedInternalQuery] = useDebounce(internalQuery, 100);
  const [open, setOpen] = useState(false);

  const updateQuery = useCallback(
    (val: string) => {
      setInternalQuery(val);
      onQueryChange?.(val);
    },
    [onQueryChange],
  );

  const dismiss = useCallback(() => {
    setOpen(false);
    updateQuery('');
  }, [updateQuery]);

  const handleValueChange = useCallback(
    (next: TValue | null) => {
      if (next == null) return;
      setInternalToken(
        options?.find((option) => option.value === next) ?? {
          value: next,
          label: next.toString(),
        },
      );
      onValueChange?.(next);
      updateQuery('');
    },
    [options, onValueChange, updateQuery],
  );

  const onRemove = useCallback(() => {
    setInternalToken(undefined);
    onValueChange?.(undefined);
    inputRef.current?.focus();
  }, [onValueChange]);

  const handleInputValueChange = useCallback(
    (next: string, details: Combobox.Root.ChangeEventDetails) => {
      if (details.reason === 'input-change') {
        updateQuery(next);
        if (next.trim()) setOpen(true);
      } else if (next === '') {
        updateQuery('');
      } else {
        details.cancel();
      }
    },
    [updateQuery],
  );

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        dismiss();
      } else if (!token) {
        setOpen(true);
      }
    },
    [dismiss, token],
  );

  const filteredOptions = useMemo(() => {
    if (isControlled || !debouncedInternalQuery) return options;

    const lowerCaseQuery = debouncedInternalQuery.toLowerCase();

    return (
      options
        ?.filter((option) => option.label.toLowerCase().includes(lowerCaseQuery))
        // Only show up to 100 options at once
        .slice(0, 100)
    );
  }, [isControlled, options, debouncedInternalQuery]);

  const canCreate = typeof creatable === 'function' ? creatable(internalQuery) : creatable;
  const addValue = !filteredOptions?.length && canCreate && internalQuery ? internalQuery : null;
  const showHint = !!minSearchLength && debouncedInternalQuery.length < minSearchLength;
  const listedOptions = (!showHint && filteredOptions) || NO_OPTIONS;
  const itemValues = useItemValues(addValue, listedOptions);
  const hasContent = (filteredOptions?.length ?? 0) > 0 || addValue != null;

  const handleInputKeyDown = useCallback(
    (event: BaseUIEvent<KeyboardEvent<HTMLInputElement>>) => {
      if (event.key === 'Escape') {
        event.preventBaseUIHandler();
        if (open) {
          if (hasContent) event.stopPropagation();
          dismiss();
        }
      }
      inputProps?.onKeyDown?.(event);
    },
    [open, hasContent, dismiss, inputProps],
  );

  const handleInputFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      setOpen(true);
      inputProps?.onFocus?.(event);
    },
    [inputProps],
  );

  const handleInputBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      if (open && !hasContent) dismiss();
      inputProps?.onBlur?.(event);
    },
    [open, hasContent, dismiss, inputProps],
  );

  return (
    <Combobox.Root<TValue, false, TValue | string>
      autoHighlight
      disabled={inputProps?.disabled}
      open={open}
      onOpenChange={handleOpenChange}
      value={token?.value ?? null}
      onValueChange={handleValueChange}
      inputValue={query ?? internalQuery}
      onInputValueChange={handleInputValueChange}
      filteredItems={itemValues}
    >
      <TokenInputWrapper
        onKeyDown={onKeyDown}
        css={merge(widthCSS(width), maxWidthCSS(maxWidth), minWidthCSS(minWidth))}
      >
        <TokenInputContainer size={controlSize}>
          {token ? (
            <SingleTokenInputTokens
              {...tagListProps}
              items={[token]}
              onRemove={onRemove}
              size={size}
            >
              <SingleTokenInputToken
                {...getTokenProps(token)}
                id={token.value}
                key={token.value}
                size={controlSize}
              >
                {token.display ?? token.label}
              </SingleTokenInputToken>
            </SingleTokenInputTokens>
          ) : (
            <TokenInputField
              size={controlSize}
              isNested
              shouldApplyFocusStyles={false}
              inputRef={inputRef}
              inputProps={{
                ...inputProps,
                placeholder: inputProps?.placeholder ?? 'Select...',
                as: Combobox.Input,
                onKeyDown: handleInputKeyDown,
                onFocus: handleInputFocus,
                onBlur: handleInputBlur,
                style: loading ? { ...inputProps?.style, paddingRight: 28 } : inputProps?.style,
              }}
            />
          )}
          {loading ? <TokenInputLoadingSpinner /> : null}
        </TokenInputContainer>
      </TokenInputWrapper>
      {!token && hasContent && (
        <TokenInputPopover
          addValue={addValue}
          options={listedOptions}
          minSearchLength={showHint ? minSearchLength : null}
        />
      )}
    </Combobox.Root>
  );
}
